import React, { useState, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';

import {
  PricesSectionType,
  PricesAccordionTab,
  PricesTabType,
  PricesYearFilter,
  PricesBedsFilter,
} from 'store/state/selectors/insights/prices';
import { useLocale } from 'locale';
import { PricesSummaryFiltersWrapper, PricesMobileFilterWrapper, PricesSummaryFilterWrapper, ScrollableWrapper } from './styled';
import FilterSummaryButton from 'components/filters/styled/FilterSummaryButton';
import { MobileExpandedWrapper } from 'components/filters/MobileWrapper';
import { H3, SmallText } from 'ds/components/typography';
import { FilterPopupTitle, FilterPopupContainer, FilterInnerContainer, FilterButtonsContainer } from 'components/filters/styled/dropdown';
import { SliderRange } from 'ds/components/slider';
import { ToggleField } from 'ds/components/input/ToggleField';
import { ToggleButton } from 'ds/components/input/Select/styled';
import { ClickAwayListener } from 'components/authentication/ClickAwayListener';
import { CleanButton } from 'ds/components/button';
import ChevronDown from 'assets/svg/chevron-down.svg';
import { ShowAt } from 'consts/breakpoints';
import { config as filtersConfig } from 'components/filters/config';
import {
  setPricesBedsFilter,
  setPricesFinalTransactionsFilter,
  setPricesYearFilter,
} from 'store/state/insightsContext/actions';
import {
  pricesBedsFilterSelector,
  pricesFinalTransactionsFilterSelector,
  pricesYearFilterSelector,
} from 'store/state/insightsContext/selectors';
import { State } from 'store/state';
import RangeSelect from 'ds/components/input/RangeSelect';
import { normalizeRoomsRange } from 'components/filters/utils';
import { marketplaceSelector, routeSelector } from 'store/state/selectors/router';
import { setAuthModal } from 'store/state/app/actions';
import { Route, State as RouteState } from 'config/routes';
import { AuthenticationModalPayload, AuthenticationModalType } from 'components/authentication/types';
import { AuthModalSource } from 'store/state/app';
import { isConnectedUserSelector } from 'store/state/domainData/selectors';

interface PricesSummaryFiltersProps {
  sectionId: PricesSectionType;
  tab: PricesAccordionTab;
  yearFilter: PricesYearFilter;
  bedsFilter: PricesBedsFilter;
  showOnlyFinalTransactionsFilter: boolean;
  onSetPricesBedsFilter: (beds: PricesBedsFilter) => void;
  onSetPricesYearFilter: (year: PricesYearFilter) => void;
  onSetPricesFinalTransactionsFilter: (isFinal: boolean) => void;
  isConnectedUser?: boolean;
  onSetAuthModal?: typeof setAuthModal;
  route: RouteState;
}

const YEAR_FILTER_MAX = new Date().getFullYear();
const YEAR_FILTER_MIN = 2004;

enum ActiveFilter {
  Beds = 'beds',
  Year = 'year',
  Mobile = 'mobile',
}

const PricesSummaryFilters: React.FC<PricesSummaryFiltersProps> = ({
  showOnlyFinalTransactionsFilter,
  bedsFilter,
  yearFilter,
  tab,
  sectionId,
  onSetPricesBedsFilter,
  onSetPricesYearFilter,
  onSetPricesFinalTransactionsFilter,
  isConnectedUser,
  onSetAuthModal,
  route,
}) => {
  const marketplace = useSelector(marketplaceSelector);
  const { t } = useLocale();
  const [ activeFilter, setActiveFilter ] = useState<ActiveFilter>(null);

  const handleBedsFilterChange = useCallback(
    (value: PricesBedsFilter) => onSetPricesBedsFilter(value),
    [ onSetPricesBedsFilter ]
  );

  const handleYearFilterChange = useCallback(
    (value: PricesYearFilter) => onSetPricesYearFilter(value),
    [ onSetPricesYearFilter ]
  );

  const handleOnlyFinalTransactionsFilterToggle = useCallback(
    (isChecked: boolean) => onSetPricesFinalTransactionsFilter(isChecked),
    [ onSetPricesFinalTransactionsFilter ]
  );

  let filters: Array<PricesYearFilter | PricesBedsFilter | boolean> = [ bedsFilter, yearFilter ];
  if (tab.tabId === PricesTabType.Unit) filters = [ ...filters, showOnlyFinalTransactionsFilter ];
  const activeFilters = filters.filter((item) => Array.isArray(item) ? item.filter(Boolean).length > 0 : Boolean(item)).length;

  const yearFilterLabel = yearFilter
    ? (yearFilter[0] === yearFilter[1]
        ? yearFilter[0]
        : `${yearFilter[0]} - ${yearFilter[1]}`)
    : null;

  const [ bedsFrom, bedsTo ] = normalizeRoomsRange(bedsFilter, marketplace);
  const bedsFilterLabel = t('filters.roomsShortValue', {
    isActive: bedsFrom !== null || bedsTo !== null,
    exactValue: bedsFrom === bedsTo ? bedsFrom : null,
    isRange: bedsFrom !== null && bedsTo !== null,
    bedsFrom,
    bedsTo,
  });

  const BEDROOMS_OPTIONS = filtersConfig[marketplace].roomsOptions;

  const handleCloseFilters = () => {
    setActiveFilter(null);
  };

  const handleResetFilters = () => {
    handleBedsFilterChange([ null, null ]);
    handleYearFilterChange(null);
    handleOnlyFinalTransactionsFilterToggle(false);
  };

  if (sectionId === PricesSectionType.ComparableListings || tab.tabId === PricesTabType.Compare || tab.tabId === PricesTabType.CompareDeals) return null;

  const genFilterClick = (filter: ActiveFilter) => (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isConnectedUser && [ Route.AddressPage, Route.LocalPage, Route.StreetPage ].includes(route.name)) {
      onSetAuthModal({ type: AuthenticationModalType.GatedDeals }, { source: AuthModalSource.DealsGateTab });
    }
    else {
      setActiveFilter(filter);
    }
  };

  return (
    <>
      <ShowAt at={1}>
        <PricesSummaryFiltersWrapper>
          <FilterSummaryButton filtersNumber={activeFilters} onClick={genFilterClick(ActiveFilter.Mobile)} />
          {activeFilter === ActiveFilter.Mobile ? (
            <MobileExpandedWrapper onClose={handleCloseFilters} onReset={handleResetFilters} onCommit={() => undefined}>
              <PricesMobileFilterWrapper>
                <H3 weight="bold">{t('filters.bedrooms')}</H3>
                <ScrollableWrapper>
                  <RangeSelect
                    values={bedsFilter}
                    options={BEDROOMS_OPTIONS.map(option => ({ value: option, label: t('filters.bedroomsOptions', { value: option }) }))}
                    onChange={(value: PricesBedsFilter) => handleBedsFilterChange(value)}
                    size="large"
                  />
                </ScrollableWrapper>
              </PricesMobileFilterWrapper>
              <PricesMobileFilterWrapper>
                <H3 weight="bold">{t('widget.prices.yearFilterMobile')}</H3>
                <FilterPopupTitle style={{ direction: 'ltr' }}>
                  {(yearFilter || [ YEAR_FILTER_MIN, YEAR_FILTER_MAX ]).join(' - ')}
                </FilterPopupTitle>
                <SliderRange
                  values={yearFilter || [ YEAR_FILTER_MIN, YEAR_FILTER_MAX ]}
                  step={1}
                  min={YEAR_FILTER_MIN}
                  max={YEAR_FILTER_MAX}
                  minDelta={0}
                  onChange={handleYearFilterChange}
                />
              </PricesMobileFilterWrapper>
              {tab.tabId === PricesTabType.Unit ? (
                <PricesMobileFilterWrapper>
                  <ToggleField
                    onClick={() => handleOnlyFinalTransactionsFilterToggle(!showOnlyFinalTransactionsFilter)}
                    label={t('widget.prices.showOnlyFinalTransactions')}
                    checked={showOnlyFinalTransactionsFilter}
                  />
                </PricesMobileFilterWrapper>
              ) : null}
            </MobileExpandedWrapper>
          ) : null}
        </PricesSummaryFiltersWrapper>
      </ShowAt>
      <ShowAt from={2}>
        <PricesSummaryFiltersWrapper>

          <SmallText>{t('widget.prices.filterBy')}</SmallText>
          <PricesSummaryFilterWrapper>
            <ToggleButton size="medium" onClick={genFilterClick(ActiveFilter.Beds)}>
              <SmallText style={{ whiteSpace: 'nowrap' }}>{bedsFilterLabel}</SmallText>
              <ChevronDown />
            </ToggleButton>
            {activeFilter === ActiveFilter.Beds ? (
              <ClickAwayListener onClickAway={handleCloseFilters}>
                <FilterPopupContainer>
                  <FilterInnerContainer withOutPadding={false}>
                    <FilterPopupTitle>{t('filters.bedrooms')}</FilterPopupTitle>
                    <RangeSelect
                      values={bedsFilter}
                      options={BEDROOMS_OPTIONS.map(option => ({ value: option, label: t('filters.bedroomsOptions', { value: option }) }))}
                      onChange={(value: PricesBedsFilter) => handleBedsFilterChange(value)}
                      size="large"
                      mode="default"
                    />
                  </FilterInnerContainer>
                  <FilterButtonsContainer>
                    <CleanButton mode="secondary" onClick={() => (handleBedsFilterChange([ null, null ]), handleCloseFilters())}>{t('filters.clear')}</CleanButton>
                    <CleanButton onClick={handleCloseFilters}>{t('filters.apply')}</CleanButton>
                  </FilterButtonsContainer>
                </FilterPopupContainer>
              </ClickAwayListener>
            ) : null}
          </PricesSummaryFilterWrapper>

          <PricesSummaryFilterWrapper>
            <ToggleButton size="medium" onClick={genFilterClick(ActiveFilter.Year)}>
              <SmallText style={{ whiteSpace: 'nowrap', direction: 'ltr' }}>{t('widget.prices.yearFilterButton', { amount: yearFilterLabel })}</SmallText>
              <ChevronDown />
            </ToggleButton>
            {activeFilter === ActiveFilter.Year ? (
              <ClickAwayListener onClickAway={handleCloseFilters}>
                <FilterPopupContainer>
                  <FilterInnerContainer withOutPadding={false}>
                    <FilterPopupTitle style={{ direction: 'ltr' }}>
                      {t('widget.prices.yearFilterTitle', { amount: (yearFilter || [ YEAR_FILTER_MIN, YEAR_FILTER_MAX ]).join(' - ') })}
                    </FilterPopupTitle>
                    <SliderRange
                      values={yearFilter || [ YEAR_FILTER_MIN, YEAR_FILTER_MAX ]}
                      step={1}
                      min={YEAR_FILTER_MIN}
                      max={YEAR_FILTER_MAX}
                      minDelta={0}
                      onChange={handleYearFilterChange}
                    />
                  </FilterInnerContainer>
                  <FilterButtonsContainer>
                    <CleanButton mode="secondary" onClick={() => (handleYearFilterChange(null), handleCloseFilters())}>{t('filters.clear')}</CleanButton>
                    <CleanButton onClick={() => setActiveFilter(null)}>{t('filters.apply')}</CleanButton>
                  </FilterButtonsContainer>
                </FilterPopupContainer>
              </ClickAwayListener>
            ) : null}
          </PricesSummaryFilterWrapper>

          {tab.tabId === PricesTabType.Unit ? (
            <ToggleField
              onClick={() => handleOnlyFinalTransactionsFilterToggle(!showOnlyFinalTransactionsFilter)}
              label={t('widget.prices.showOnlyFinalTransactions')}
              checked={showOnlyFinalTransactionsFilter}
            />
          ) : null}
        </PricesSummaryFiltersWrapper>
      </ShowAt>
    </>
  );
};

const mapDispatchToProps = {
  onSetPricesBedsFilter: setPricesBedsFilter,
  onSetPricesYearFilter: setPricesYearFilter,
  onSetPricesFinalTransactionsFilter: setPricesFinalTransactionsFilter,
  onSetAuthModal: (payload: AuthenticationModalPayload, meta: { isUserInitiated?: boolean, source?: AuthModalSource }) => setAuthModal(payload, meta),
};

const mapStateToProps = (state: State) => ({
  yearFilter: pricesYearFilterSelector(state),
  bedsFilter: pricesBedsFilterSelector(state),
  showOnlyFinalTransactionsFilter: pricesFinalTransactionsFilterSelector(state),
  isConnectedUser: isConnectedUserSelector(state),
  route: routeSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(PricesSummaryFilters);
